export const ShouldRequestMixed3pcExperian = ({
    hasExperian,
    has3P,
    has3PCustom,
    hasThirdParty,
    trackingOn,
    marketplaceOn,
    exportOn,
    visibleOn,
}: {
    hasExperian: boolean;
    has3P: boolean;
    has3PCustom: boolean;
    hasThirdParty: boolean;
    trackingOn: boolean;
    marketplaceOn: boolean;
    exportOn: boolean;
    visibleOn: boolean;
}): boolean => {
    // Must have Experian data
    if (!hasExperian) return false;

    // Must NOT have 3P
    if (has3P) return false;

    // Must have 3PC (3P Custom)
    if (!has3PCustom) return false;

    // Must have Export and Visible both ON
    if (!exportOn || !visibleOn) return false;

    if (hasExperian && !hasThirdParty && has3PCustom && visibleOn && exportOn) return true;

    // Tracking must be OFF, OR (Tracking ON and Marketplace OFF)
    const trackingCondition = !trackingOn || (trackingOn && !marketplaceOn);

    return trackingCondition;
};

export const ShouldRequestThirdpAccess = ({
    hasExperian,
    has3P,
    has1P,
    has3PCustom,
    trackingOn,
    marketplaceOn,
    exportOn,
    visibleOn,
    isRequestable,
}: {
    hasExperian: boolean;
    has3P: boolean;
    has1P: boolean;
    has3PCustom: boolean;
    trackingOn: boolean;
    marketplaceOn: boolean;
    exportOn: boolean;
    visibleOn: boolean;
    isRequestable: boolean;
}): boolean => {
    if (isRequestable) return false;
    // Must have Export and Visible both ON
    if (!exportOn || !visibleOn) return false;

    // Scenario 1: TrackingID OFF + Marketplace ON + Export ON + Visible ON
    if (!trackingOn && marketplaceOn && exportOn && visibleOn) {
        // ERROR 3 if: Experian mixed with (3P OR 1P OR 3PC), OR just 3P present
        if (hasExperian && (has3P || has1P || has3PCustom) && !(!has3P && has1P && !has3PCustom)) return true;
        if (has3P && !hasExperian && !has1P && !has3PCustom) return true; // Just 3P alone
        if (has3P && (has1P || has3PCustom)) return true; // 3P mixed with others (no Experian)
        return false;
    }

    // Scenario 2: TrackingID OFF + Marketplace OFF + Export ON + Visible ON
    if (!trackingOn && !marketplaceOn && exportOn && visibleOn && has3P) return true;

    // Experian - false, hasThirdParty && hasFirstParty && has3PCustom - true, visibleOn && exportOn - true
    if (!hasExperian && has3P && has1P && has3PCustom && visibleOn && exportOn) {
        if (!trackingOn && marketplaceOn) return true;
        if (trackingOn && !marketplaceOn) return true;
        if (!trackingOn && !marketplaceOn) return true;
        if (trackingOn && marketplaceOn) return false;
    }

    if (hasExperian && has3P && has1P && visibleOn && exportOn) return true;

    // Scenario 3: TrackingID ON + Marketplace OFF + Export ON + Visible ON
    if (trackingOn && !marketplaceOn && exportOn && visibleOn) {
        // ERROR 3 if: Experian mixed with (3P OR 3PC), OR just 3P present
        if (hasExperian && (has3P || has3PCustom)) return true;
        if (has3P && !hasExperian && !has3PCustom) return true; // Just 3P alone
        if (has3P && has3PCustom && !hasExperian) return true; // 3P + 3PC (no Experian)
        return false;
    }

    return false;
};

export const ShouldRequestDestinationAccess = ({
    trackingOn,
    marketplaceOn,
    exportOn,
    visibleOn,
}: {
    trackingOn: boolean;
    marketplaceOn: boolean;
    exportOn: boolean;
    visibleOn: boolean;
}): boolean => {
    // ERROR 2 only occurs when Export is OFF and Visible is ON
    if (!(!exportOn && visibleOn)) {
        return false;
    }

    // Scenario 1: Tracking OFF + Marketplace ON + Export OFF + Visible ON
    if (!trackingOn && marketplaceOn && !exportOn && visibleOn) {
        // Any combination of audience data triggers ERROR 2
        return true;
    }

    // Scenario 2: Tracking OFF + Marketplace OFF + Export OFF + Visible ON
    if (!trackingOn && !marketplaceOn && !exportOn && visibleOn) {
        // Any combination of audience data triggers ERROR 2
        return true;
    }

    // Scenario 3: Tracking ON + Marketplace ON + Export OFF + Visible ON
    if (trackingOn && marketplaceOn && !exportOn && visibleOn) {
        // Any combination of audience data triggers ERROR 2
        return true;
    }

    // Scenario 4: Tracking ON + Marketplace OFF + Export OFF + Visible ON
    if (trackingOn && !marketplaceOn && !exportOn && visibleOn) {
        // Any combination of audience data triggers ERROR 2
        return true;
    }

    return false;
};

export const ShouldGrayOut = ({
    hasExperian,
    has3P,
    has1P,
    has3PCustom,
    trackingOn,
    marketplaceOn,
    exportOn,
    visibleOn,
    indirectDest,
    isValidForIndirect,
}: {
    hasExperian: boolean;
    has3P: boolean;
    has1P: boolean;
    has3PCustom: boolean;
    trackingOn: boolean;
    marketplaceOn: boolean;
    exportOn: boolean;
    visibleOn: boolean;
    indirectDest: boolean;
    isValidForIndirect: boolean;
}): boolean => {
    // If not visible or not export capable, matrix rows say "No" (do not gray out)
    if (!visibleOn || !exportOn) return false;

    // Gray out indirect destinations that are not valid for indirect export
    if (indirectDest && !isValidForIndirect) {
        return true;
    }

    if (!marketplaceOn && exportOn && visibleOn && trackingOn) {
        // 3P (with or without Experian/1P/3PC)
        return has3P;
    }

    // Experian on and third party, first party, and 3P custom off: do not gray out
    if (hasExperian && !has3P && !has1P && !has3PCustom) return false;

    // Experian, Third party, first party off but 3P custom on: do not gray out
    if (!hasExperian && !has3P && !has1P && has3PCustom) return false;

    // Gray out: Experian + 1P + 3PC with Tracking OFF
    if (hasExperian && has1P && has3PCustom && visibleOn && exportOn && !trackingOn) return true;

    // Do not gray out: Experian + 1P only (no 3P or 3PC)
    if (hasExperian && !has3P && has1P && !has3PCustom && visibleOn && exportOn) return false;

    // 3P + 1P + 3PC (no Experian): Gray out except when Tracking ON + Marketplace ON
    if (!hasExperian && has3P && has1P && has3PCustom && visibleOn && exportOn) {
        if (!trackingOn && marketplaceOn) return true;
        if (trackingOn && !marketplaceOn) return true;
        if (!trackingOn && !marketplaceOn) return true;
        if (trackingOn && marketplaceOn) return false;
    }

    // 3P only (no Experian, 1P, or 3PC): Gray out except when Tracking ON + Marketplace ON
    if (!hasExperian && has3P && !has1P && !has3PCustom) {
        if (!trackingOn) return true;
        if (!marketplaceOn && trackingOn) return true;
        if (marketplaceOn && trackingOn) return false;
    }

    // Do not gray out: 3P + 1P (no Experian or 3PC) with Tracking ON + Marketplace ON
    if (!hasExperian && has3P && has1P && !has3PCustom && trackingOn && marketplaceOn) return false;

    // Gray out: 3P + 1P (no Experian or 3PC) in all other cases
    if (!hasExperian && has3P && has1P && !has3PCustom) return true;

    // 3P + 3PC (no Experian or 1P): Gray out except when Tracking ON + Marketplace ON
    if (!hasExperian && has3P && !has1P && has3PCustom && visibleOn && exportOn) {
        if (!trackingOn) return true;
        if (!marketplaceOn && trackingOn) return true;
        if (marketplaceOn && trackingOn) return false;
    }

    // Gray out: Tracking OFF + Marketplace ON when Experian is mixed with any other audience type
    if (!trackingOn && marketplaceOn && exportOn && visibleOn) {
        const mixesWithOther = has3P || has1P || has3PCustom;
        return hasExperian && mixesWithOther;
    }

    // Gray out: Tracking OFF (regardless of Marketplace) when Experian is mixed with 3P or 3PC
    if (!trackingOn && exportOn && visibleOn) {
        const mixesWith3P = has3P || has3PCustom;
        return hasExperian && mixesWith3P;
    }

    // Otherwise do not gray out
    return false;
};
